'use client';

import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { Flag } from '@packages/shared/src/ui/Flag/Flag';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { DialogHeader } from '../Dialog/DialogHeader/DialogHeader.client';
import { useServiceLayerTracking } from '../Tracking/hooks/useServiceLayerTracking/useServiceLayerTracking';
import { ecoFriendlyDeliveryMessages } from './messages';

export type EcoFriendlyDeliveryProps = {
  serviceLayer: ReactNode;
};

/**
 * EcoFriendlyDelivery component
 *
 * serviceLayer: ServiceLayer component from packages/cms-components ->
 * is needed because package/modules is not allowed to import from packages/cms-components
 * */

export const EcoFriendlyDelivery = ({ serviceLayer }: EcoFriendlyDeliveryProps) => {
  const { formatMessage } = useIntl();
  const {
    product: {
      ecoFriendlyDelivery: { enabled, serviceLayerID },
    },
  } = useConfig();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const seenTracked = useRef(false);

  const { trackEcoFriendlyDeliveryClick, trackEcoFriendlyDeliverySeen, trackDisplayOverlay } =
    useServiceLayerTracking();

  if (!enabled) {
    return null;
  }

  if (enabled && !seenTracked.current) {
    trackEcoFriendlyDeliverySeen(serviceLayerID);
    seenTracked.current = true;
  }

  const title = formatMessage(ecoFriendlyDeliveryMessages.dialogTitle);

  const clickHandler = () => {
    trackEcoFriendlyDeliveryClick(serviceLayerID);
    trackDisplayOverlay({ category: 'dv-service-layer-eco-friendly-delivery', label: title });
    setIsModalOpen(true);
  };

  return (
    <>
      <div
        tabIndex={0}
        role="button"
        className="text-size-body-sm cursor-pointer"
        onClick={clickHandler}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            clickHandler();
          }
        }}
      >
        {formatMessage(ecoFriendlyDeliveryMessages.co2FriendlyText, {
          flag: (
            <Flag
              className="top-[-0.1em]"
              variant="sustainability"
              text={formatMessage(ecoFriendlyDeliveryMessages.co2FriendlyFlag)}
            />
          ),
        })}
      </div>
      <Dialog isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <DialogHeader onClose={() => setIsModalOpen(false)}>
          {formatMessage(ecoFriendlyDeliveryMessages.dialogTitle)}
        </DialogHeader>
        <DialogContent>{serviceLayer}</DialogContent>
      </Dialog>
    </>
  );
};
